import React, { ReactNode } from "react";
import { ActiveElement, ChartEvent, ChartOptions } from "chart.js";
import { map, reduce } from "lodash";
import { shorten } from "@backpacker/number";
import Tooltip from "@charty/tooltip/chartjs-simple-serie";
import { Color } from "@glossy";
import { chart as chartColor, content } from "@glossy/theme";
import { getMetricLabel, tr } from "@linkfluence/i18n";
import { AbstractDate, Interval } from "@linkfluence/models/date";
import { getOnChangeCanvasCursorEvent } from "@linkfluence/models/events/onChangeCanvasCursor";
import { Locale } from "@linkfluence/models/locale";
import { Metric } from "@linkfluence/models/metric";
import { Timezone } from "@linkfluence/models/timezone";
import { isFloat } from "@tootools/number";
import { Serie } from "../models";
import {
  closeCustomTooltip,
  openCustomTooltip,
} from "../tooltip/chartjs-tooltip-root";
import { formatXAxisBottomLabel, formatXAxisTopLabel } from "../utils";

function getTotalPublications(series: Serie[]): number[] {
  return reduce(
    series,
    (totalByDate, { data }) => {
      return reduce(
        data,
        (total, { count }, i) => {
          total[i] = (total[i] || 0) + count;
          return total;
        },
        totalByDate
      );
    },
    []
  );
}

type ChartTooltip = {
  count: number;
  label: string;
  color: string;
  date: AbstractDate;
};

export function getOptions(
  allDates: AbstractDate[],
  timeUnit: Interval,
  theme,
  locale: Locale,
  timezone: Timezone,
  onClick: (datasetIndex: number, index: number) => void,
  series: Serie[],
  chartTooltip?: (props: ChartTooltip[]) => ReactNode,
  scale?: "linear" | "logarithmic",
  disableAnimation?: boolean,
  isFullscreen: boolean = false
): ChartOptions<"line"> {
  return {
    animation: {
      duration: disableAnimation ? 0 : 1000,
    },
    onClick: function (evt) {
      if (onClick) {
        const elements = this.getElementsAtEventForMode(
          evt,
          "nearest",
          { intersect: true },
          false
        );
        if (elements && elements[0]) {
          onClick(elements[0].datasetIndex, elements[0].index);
        }
      }
    },
    scales: {
      y: {
        type: scale,
        beginAtZero: true,
        ticks: {
          color: content({ theme }, "secondary"),
          callback: (value: any) =>
            isFloat(value) ? undefined : shorten(locale, value),
          padding: 6,
        },
        border: {
          display: false,
        },
        grid: {
          display: true,
          color: content({ theme }, "tertiary"),

          tickLength: 6,
        },
        position: "left",
      },
      y2: {
        beginAtZero: true,
        ticks: {
          color: chartColor({ theme }),
          callback: (value: any) =>
            isFloat(value) ? undefined : shorten(locale, value),
          padding: 6,
        },
        border: {
          display: false,
        },
        grid: {
          display: true,
          color: chartColor({ theme }),
          drawTicks: true,
          drawOnChartArea: false,

          tickLength: 6,
        },
        position: "right",
      },
      x: {
        offset: true,
        ticks: {
          color: content({ theme }, "secondary"),
          callback: formatXAxisBottomLabel(timeUnit, locale, timezone, series),
          maxRotation: 90,
          minRotation: 80,
        },
        border: {
          display: false,
        },
        grid: {
          color: "transparent",
          z: 1,
          offset: true,
          tickLength: 6,
        },
      },
      x2: {
        offset: true,
        position: "top",
        ticks: {
          autoSkip: false,
          maxRotation: 0,
          color: content({ theme }, "secondary"),
          callback: formatXAxisTopLabel(timeUnit, locale, timezone, series),
        },
        border: {
          display: false,
        },
        grid: {
          display: true,
          color: content({ theme }, "tertiary"),
          lineWidth: 0.5,
        },
      },
    },
    onHover: function (e: ChartEvent, elements: ActiveElement[]) {
      if (onClick && e.native.target instanceof HTMLElement) {
        const cursor = elements.length ? "var(--cursor-magic-wand)" : "default";
        if (e.native.target.style.cursor !== cursor) {
          e.native.target.style.cursor = cursor;
          e.native.target.dispatchEvent(getOnChangeCanvasCursorEvent());
        }
      }
    },
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        mode: "index",
        intersect: true,
        position: "nearest",
        caretPadding: 24,
        enabled: isFullscreen,
        padding: 24,
        backgroundColor: Color.Black,
        titleColor: Color.White,
        titleFont: { size: 14 },
        bodyColor: Color.White,
        bodyFont: { size: 14 },
        bodySpacing: 12,
        callbacks: {
          title: (tooltipModel) => {
            return new Date(tooltipModel[0].label).toLocaleString(
              locale.split("_")[0],
              {
                year: "numeric",
                month: "long",
                day: "numeric",
              }
            );
          },
          label: (tooltipModel) => {
            const index = tooltipModel.datasetIndex;
            if (index === 0) {
              const xMetric = tooltipModel.chart.data.datasets[0] as {
                id: string;
                data: number[];
              };
              const xScore = shorten(
                locale,
                xMetric.data[tooltipModel.dataIndex]
              );
              return ` ${tr(locale, "METRICS::" + xMetric.id)}: ${xScore}`;
            }
            const yMetric = tooltipModel.chart.data.datasets[1] as {
              id: string;
              data: number[];
            };
            const yScore = shorten(
              locale,
              yMetric.data[tooltipModel.dataIndex]
            );
            return ` ${tr(locale, "METRICS::" + yMetric.id)}: ${yScore}`;
          },
        },
        external: (context) => {
          if (isFullscreen) {
            return null;
          }

          const { chart, tooltip } = context;

          if (!tooltip.opacity) {
            closeCustomTooltip();
            return;
          }

          const dataPoints = tooltip.dataPoints;

          const totalPublications = getTotalPublications(series);
          const dataSeries = map(dataPoints, (dataPoint) => {
            const dataSetIndex = dataPoint.datasetIndex;
            const index = dataPoint.dataIndex;
            const label = series[dataSetIndex].label;
            return {
              color:
                series[dataSetIndex].type === "bar"
                  ? content({ theme }, "secondary")
                  : chartColor({ theme }),
              label: getMetricLabel(locale, label as Metric) || label,
              date: allDates[index],
              count: series[dataSetIndex].data[index].count,
              total: totalPublications[index],
            };
          });

          openCustomTooltip(
            tooltip,
            chart,
            chartTooltip ? (
              chartTooltip(dataSeries)
            ) : (
              <Tooltip series={dataSeries} locale={locale} />
            )
          );
        },
      },
      //@ts-ignore: datalabels is properly imported and registered on the chart
      datalabels: {
        display: false,
      },
    },
  };
}
